/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import {
  useImperativeHandle,
  useCallback,
  useMemo,
  useRef,
  useLayoutEffect,
  useEffect,
  useState
} from 'preact/hooks';
import { forwardRef, ForwardedRef } from 'preact/compat';
import { classNames } from '../utils/UNSAFE_classNames';
import {
  handleEnterActionableMode,
  handleWrapActionableFocus
} from '../utils/PRIVATE_collectionUtils';
import { useId } from '../hooks/UNSAFE_useId';
import { useUser } from '../hooks/UNSAFE_useUser';
import {
  Cell,
  Column,
  ColumnWidths,
  TableHorizontalScrollPosition,
  TableVerticalScrollPosition
} from '../UNSAFE_TableView';
import { TableColGroup } from './TableColGroup';
import { TableBody } from './TableBody';
import { TableHeader } from './TableHeader';
import { TableFooter } from './TableFooter';
import { TableDragIndicator } from './TableDragIndicator';
import { FocusTracker } from '../PRIVATE_FocusTracker';
import { Props, TableViewHandle } from '../UNSAFE_TableView';
import { useInteractionManager } from './hooks/useInteractionManager';
import { getElementFromCell } from './utils/TableDomUtils';
import { getLayoutColumnWidths } from './utils/TableLayoutUtils';
import {
  getViewportConfig,
  getHorizontalScrollPosition,
  setHorizontalScrollPosition,
  getVerticalScrollPosition,
  setVerticalScrollPosition,
  updateStickyColumns
} from './utils/TableScrollUtils';
import { innerTableStyles, focusTrackerStyles } from './themes/Table.css';
import { TableVariantOptions, tableStyles } from './themes/TableStyles.css';
import { TableRedwoodTheme } from './themes/redwood/TableTheme';
import { useComponentTheme } from '../hooks/UNSAFE_useComponentTheme';
import { FocusableElement, getActiveElement, getBodyElement } from '../utils/PRIVATE_tabbableUtils';
import { useResizeObserver } from '../hooks/UNSAFE_useResizeObserver';
import { useTestId } from '../hooks/UNSAFE_useTestId';

export type TableColumn<K, D, C> = {
  key: C;
  value: Column<K, D, C>;
};

export type SampledWidths<C extends string> = {
  minWidths: ColumnWidths<C>;
  weightWidths: ColumnWidths<C>;
};

export type SizingInfo =
  | {
      isInitialRender: true;
      boxWidth?: number;
      contentWidth?: number;
      boxHeight?: number;
      contentHeight?: number;
      defaultScrollbarWidth?: number;
      defaultScrollbarHeight?: number;
    }
  | {
      isInitialRender: false;
      boxWidth: number;
      contentWidth: number;
      boxHeight: number;
      contentHeight: number;
      defaultScrollbarWidth: number;
      defaultScrollbarHeight: number;
    };

type LayoutInfo =
  | {
      isLayoutRequired: true;
      columnWidthsArray?: number[];
    }
  | {
      isLayoutRequired: false;
      columnWidthsArray: number[];
    };

export type ColumnResizeInfo<C> =
  | {
      isResizing: false;
      isResizeHover: boolean;
      key?: C;
      startX?: never;
      delta?: never;
      fullColumnWidths?: never;
      ignoreClick: boolean;
    }
  | {
      isResizing: true;
      isResizeHover: boolean;
      key: C;
      startX: number;
      delta: number;
      fullColumnWidths: number[];
      ignoreClick: boolean;
    };

export type ScrollDetail<K, C> = {
  cell: Cell<K, C>;
  locationX?: 'inView' | 'start' | 'end';
  locationY?: 'inView' | 'top' | 'bottom';
};

const _defaultSelected = {
  row: { all: false, keys: new Set() },
  column: { all: false, keys: new Set() }
};

/**
 * Component that renders items as a flat table.
 * In order to maximize performance, only items that are visible in the viewport are rendered.
 */
export const Table = forwardRef(function Table<K extends string | number, D, C extends string>(
  {
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    getAccessibleRowHeaders,
    columns,
    columnOrder,
    columnWidths,
    layout = 'contents',
    data,
    getRowKey,
    hasMore = false,
    onLoadMore = () => {},
    currentCell,
    onCurrentCellChange,
    currentRowVariant,
    gridlines,
    selectionMode,
    selected = _defaultSelected as NonNullable<Props<K, D, C>['selected']>,
    onSelectionChange,
    onRowAction,
    sortCriterion,
    onSortCriterionChange,
    horizontalScrollPositionOverride,
    verticalScrollPositionOverride,
    columnResizingIndicator,
    onColumnResizing,
    onColumnResize,
    noDataRenderer,
    testId
  }: Props<K, D, C>,
  ref?: ForwardedRef<TableViewHandle<K, C>>
) {
  const tableId = useId();
  const outerTableRef = useRef<HTMLDivElement>(null);
  const sizingInfoRef = useRef<SizingInfo>({ isInitialRender: true });
  const [appliedColumnWidths, setAppliedColumnWidths] = useState<ColumnWidths<C>>();
  const [contentHeight, setContentHeight] = useState<number>();
  const isVerticalScrollRef = useRef<boolean>(false);
  const sampledColumnWidthsRef = useRef<SampledWidths<C>>();
  const horizontalScrollPositionRef = useRef<TableHorizontalScrollPosition<C>>();
  const verticalScrollPositionRef = useRef<TableVerticalScrollPosition<K>>();

  const currentStickyEdgesRef = useRef<{ start?: number; end?: number }>({
    start: undefined,
    end: undefined
  });

  const resizeInfoRef = useRef<ColumnResizeInfo<C>>({
    isResizing: false,
    isResizeHover: false,
    ignoreClick: false
  });

  const { direction } = useUser();
  const isRtl = direction === 'rtl';
  const testIdProps = useTestId(testId);

  const columnsArray = useMemo(() => {
    const newColumnsArray = [] as Array<TableColumn<K, D, C>>;
    if (columnOrder != null) {
      for (const key of columnOrder) {
        if (columns[key] != null) {
          newColumnsArray.push({
            key: key,
            value: columns[key]
          });
        }
      }
    } else {
      for (const [key, value] of Object.entries(columns)) {
        newColumnsArray.push({
          key: key as C,
          value: value as Column<K, D, C>
        });
      }
    }
    return newColumnsArray;
  }, [columnOrder, columns]);

  let isLayoutRequired = false;
  const columnWidthsArray = [];
  if (appliedColumnWidths != null) {
    for (let i = 0; i < columnsArray.length; i++) {
      const key = columnsArray[i].key;
      const appliedWidth = appliedColumnWidths[key];
      const staticWidth = columnWidths != null ? columnWidths[key] : undefined;
      if (appliedWidth == null || (staticWidth != null && staticWidth !== appliedWidth)) {
        isLayoutRequired = true;
        break;
      }
      columnWidthsArray.push(appliedWidth);
    }
  } else {
    isLayoutRequired = true;
  }
  const layoutInfo = {
    isLayoutRequired: isLayoutRequired,
    columnWidthsArray: isLayoutRequired ? undefined : columnWidthsArray
  } as LayoutInfo;

  useImperativeHandle(
    ref!,
    () => {
      return {
        getHorizontalScrollPosition: () => {
          if (layoutInfo.isLayoutRequired) {
            return {};
          }
          return getHorizontalScrollPosition(
            outerTableRef.current!,
            columnsArray,
            layoutInfo.columnWidthsArray
          );
        },
        getVerticalScrollPosition: () => {
          if (layoutInfo.isLayoutRequired) {
            return {};
          }
          return getVerticalScrollPosition(outerTableRef.current!, tableId);
        }
      };
    },
    [tableId, columnsArray, layoutInfo.isLayoutRequired, layoutInfo.columnWidthsArray]
  );

  const cellHasRenderer = useCallback(
    (currentCell: Cell<K, C>) => {
      let hasRenderer = false;
      if (currentCell.type === 'noData') {
        hasRenderer = noDataRenderer != null;
      } else {
        for (const column of columnsArray) {
          if (column.key === currentCell.columnKey) {
            if (currentCell.type === 'data') {
              hasRenderer = column.value.renderer != null;
            } else if (currentCell.type === 'header') {
              hasRenderer = column.value.headerRenderer != null;
            } else if (currentCell.type === 'footer') {
              hasRenderer = column.value.footerRenderer != null;
            }
            break;
          }
        }
      }
      return hasRenderer;
    },
    [columnsArray, noDataRenderer]
  );

  const {
    interactionManagerProps,
    onTableSelectionChange,
    showFocusRing,
    isCurrentTabbableCell,
    getCurrentTabbableCell,
    setCurrentTabbableCell
  } = useInteractionManager({
    outerTableRef,
    tableId,
    data,
    isRtl,
    columnsArray,
    appliedColumnWidths: appliedColumnWidths!,
    sizingInfoRef,
    resizeInfoRef,
    currentStickyEdgesRef,
    cellHasRenderer,
    fullColumnWidths: layoutInfo.isLayoutRequired ? undefined : layoutInfo.columnWidthsArray,
    onColumnResizing,
    onColumnResize,
    currentCell,
    onCurrentCellChange,
    selectionMode,
    selected,
    onSelectionChange,
    onRowAction,
    sortCriterion,
    onSortCriterionChange
  });

  useEffect(() => {
    // if focus was somehow dropped to the body during the render cycle - recapture focus here
    if (
      outerTableRef.current != null &&
      getCurrentTabbableCell() != null &&
      getActiveElement(outerTableRef.current) === getBodyElement(outerTableRef.current)
    ) {
      outerTableRef.current.focus({ preventScroll: true });
    }
  });

  // ensure sampled column sizes are cleared if 'columns' is updated
  useLayoutEffect(() => {
    sampledColumnWidthsRef.current = undefined;
    setAppliedColumnWidths(undefined);
  }, [columns]);

  // setup sizing calculation updates for subsequent renders
  useLayoutEffect(() => {
    const rootElement = outerTableRef.current;
    if (
      rootElement != null &&
      !sizingInfoRef.current.isInitialRender &&
      layoutInfo.isLayoutRequired
    ) {
      const totalWidth =
        layout === 'fixed'
          ? sizingInfoRef.current.boxWidth - sizingInfoRef.current.defaultScrollbarWidth
          : -1;
      const newColumnWidths = getLayoutColumnWidths(
        rootElement,
        tableId,
        layout === 'fixed',
        sampledColumnWidthsRef,
        totalWidth,
        columnsArray,
        columnWidths
      );
      setAppliedColumnWidths(newColumnWidths);
    }
  }, [tableId, columnsArray, columnWidths, layout, layoutInfo.isLayoutRequired]);

  // setup component resize-based sizing calculation updates
  const resizeObserverCallbackRef = useRef<(entry: ResizeObserverEntry) => void>();
  resizeObserverCallbackRef.current = (entry: ResizeObserverEntry) => {
    if (outerTableRef.current != null) {
      let availableWidth;
      const newContentWidth = entry.contentBoxSize[0].inlineSize;
      const newContentHeight = entry.contentBoxSize[0].blockSize;
      const newBoxWidth = entry.borderBoxSize[0].inlineSize;
      const newBoxHeight = entry.borderBoxSize[0].blockSize;

      if (sizingInfoRef.current.isInitialRender) {
        availableWidth = layout === 'fixed' ? newContentWidth : -1;
        sizingInfoRef.current = {
          isInitialRender: false,
          contentWidth: newContentWidth,
          boxWidth: newBoxWidth,
          contentHeight: newContentHeight,
          boxHeight: newBoxHeight,
          defaultScrollbarWidth: newBoxWidth - newContentWidth,
          defaultScrollbarHeight: newBoxHeight - newContentHeight
        };
      } else {
        availableWidth = isVerticalScrollRef.current
          ? newContentWidth
          : newBoxWidth - sizingInfoRef.current.defaultScrollbarWidth;
        sizingInfoRef.current.contentWidth = newContentWidth;
        sizingInfoRef.current.boxWidth = newBoxWidth;
        sizingInfoRef.current.contentHeight = newContentHeight;
        sizingInfoRef.current.boxHeight = newBoxHeight;
      }
      const newColumnWidths = getLayoutColumnWidths(
        outerTableRef.current,
        tableId,
        layout === 'fixed',
        sampledColumnWidthsRef,
        availableWidth,
        columnsArray,
        columnWidths
      );
      setAppliedColumnWidths(newColumnWidths);
      if (contentHeight !== newContentHeight) {
        setContentHeight(newContentHeight);
      }
    }
  };
  // ensure resize observer callback function is static to avoid hook re-subscribing each render
  const staticResizeObserverCallback = useCallback((entry: ResizeObserverEntry) => {
    resizeObserverCallbackRef.current!(entry);
  }, []);
  useResizeObserver({ ref: outerTableRef, callback: staticResizeObserverCallback });

  // after each additional render, update vertical scrollbar state if necessary
  useLayoutEffect(() => {
    if (
      outerTableRef.current != null &&
      !sizingInfoRef.current.isInitialRender &&
      contentHeight != null
    ) {
      const innerTable = outerTableRef.current.querySelector(
        `[data-oj-table-inner-table='${tableId}']`
      );
      if (innerTable != null) {
        const hasVerticalOverflow = contentHeight < innerTable.getBoundingClientRect().height;
        if (isVerticalScrollRef.current !== hasVerticalOverflow) {
          isVerticalScrollRef.current = hasVerticalOverflow;
          if (hasVerticalOverflow) {
            outerTableRef.current.classList.remove(tableStyles.noVerticalScroll);
            outerTableRef.current.classList.add(tableStyles.verticalScroll);
          } else {
            outerTableRef.current.classList.remove(tableStyles.verticalScroll);
            outerTableRef.current.classList.add(tableStyles.noVerticalScroll);
          }
        }
      }
    }
  }, [tableId, contentHeight, data, noDataRenderer, appliedColumnWidths]);

  // update horizontal scroll position if a new value is provided
  useLayoutEffect(() => {
    if (
      horizontalScrollPositionRef.current !== horizontalScrollPositionOverride &&
      !layoutInfo.isLayoutRequired
    ) {
      setHorizontalScrollPosition(
        outerTableRef.current!,
        columnsArray,
        layoutInfo.columnWidthsArray,
        isRtl,
        horizontalScrollPositionOverride
      );
      horizontalScrollPositionRef.current = horizontalScrollPositionOverride;
    }
  }, [
    tableId,
    columnsArray,
    layoutInfo.isLayoutRequired,
    layoutInfo.columnWidthsArray,
    horizontalScrollPositionOverride,
    isRtl
  ]);

  // update vertical scroll position if a new value is provided
  useLayoutEffect(() => {
    if (
      verticalScrollPositionRef.current !== verticalScrollPositionOverride &&
      !layoutInfo.isLayoutRequired
    ) {
      setVerticalScrollPosition(outerTableRef.current!, tableId, verticalScrollPositionOverride);
      verticalScrollPositionRef.current = verticalScrollPositionOverride;
    }
  }, [tableId, layoutInfo.isLayoutRequired, verticalScrollPositionOverride]);

  // ensure sticky columns are updated appropriately
  useLayoutEffect(() => {
    if (outerTableRef.current != null && !layoutInfo.isLayoutRequired) {
      updateStickyColumns(
        outerTableRef.current,
        tableId,
        isRtl,
        columnsArray,
        layoutInfo.columnWidthsArray,
        currentStickyEdgesRef,
        true
      );
    }
  }, [tableId, columnsArray, layoutInfo.isLayoutRequired, layoutInfo.columnWidthsArray, isRtl]);

  // initialize base Table style classes
  const { classes: themeClasses } = useComponentTheme<TableVariantOptions>(TableRedwoodTheme, {
    forceScroll: sizingInfoRef.current.isInitialRender ? 'isForceScroll' : 'notForceScroll',
    verticalScroll: isVerticalScrollRef.current ? 'isVerticalScroll' : 'notVerticalScroll',
    disableWrapping:
      layout === 'contents' && sampledColumnWidthsRef.current === undefined
        ? 'isDisableWrapping'
        : 'notDisableWrapping'
  });

  const classes = classNames([themeClasses]);
  const tableClasses = classNames([innerTableStyles.base]);
  const focusTrackerClasses = classNames([focusTrackerStyles.base]);

  // ensure aria-activedescendant is applied to the 'currentCell' at all times
  useEffect(() => {
    if (outerTableRef.current != null && currentCell != null) {
      const cellElement = getElementFromCell(outerTableRef.current, tableId, currentCell);
      if (cellElement != null) {
        outerTableRef.current.setAttribute('aria-activedescendant', cellElement.id);
      }
    }
  }, [tableId, currentCell]);

  // aria-rowcount includes header and footer rows
  const hasFooters = columnsArray.some((column: TableColumn<K, D, C>) => {
    return column.value.footerRenderer != null || column.value.footerText != null;
  });
  const ariaRowCount = data == null ? undefined : hasMore ? -1 : data.length + (hasFooters ? 2 : 1);

  const startStickyEdge = currentStickyEdgesRef.current?.start;
  const endStickyEdge = currentStickyEdgesRef.current?.end;

  const hasVerticalGridlines = gridlines?.vertical === 'visible';
  const hasHorizontalGridlines = gridlines?.horizontal !== 'hidden';

  const isRowSelectionEnabled =
    selectionMode?.row === 'single' || selectionMode?.row === 'multiple';
  const isColumnSelectionEnabled =
    selectionMode?.column === 'single' || selectionMode?.column === 'multiple';

  const getTableBodyProps = () => {
    return {
      tableId: tableId,
      data: data,
      columnsArray: columnsArray,
      getAccessibleRowHeaders: getAccessibleRowHeaders,
      hasVerticalGridlines: hasVerticalGridlines,
      hasHorizontalGridlines: hasHorizontalGridlines,
      selected: selected,
      isRowSelectionEnabled: isRowSelectionEnabled,
      isSingleRowSelectionEnabled: selectionMode?.row === 'single',
      currentCell: currentCell,
      currentRowVariant: currentRowVariant,
      getCurrentTabbableCell: getCurrentTabbableCell,
      showFocusRing: showFocusRing,
      getRowKey: getRowKey,
      hasMore: hasMore,
      onLoadMore: onLoadMore,
      noDataRenderer: noDataRenderer,
      viewportConfig: getViewportConfig(outerTableRef),
      onSelectionChange: onTableSelectionChange,
      startStickyEdge: startStickyEdge,
      endStickyEdge: endStickyEdge,
      isRtl: isRtl
    };
  };

  const getTableColGroupProps = () => {
    let columnWidthsArray;
    if (!layoutInfo.isLayoutRequired) {
      columnWidthsArray = layoutInfo.columnWidthsArray;
    } else {
      columnWidthsArray = [];
      for (let i = 0; i < columnsArray.length; i++) {
        columnWidthsArray[i] = undefined;
      }
    }
    return {
      tableId: tableId,
      columnWidthsArray: columnWidthsArray
    };
  };

  const getTableHeaderProps = () => {
    const tabbableCell = getCurrentTabbableCell();
    const containsTabbableCell = tabbableCell?.type === 'header';
    return {
      tableId: tableId,
      columnsArray: columnsArray,
      hasVerticalGridlines: hasVerticalGridlines,
      focusedKey: currentCell?.type === 'header' ? currentCell.columnKey : undefined,
      selectedColumnKeys: selected.column,
      selectedRowKeys: selected.row,
      isColumnSelectionEnabled: isColumnSelectionEnabled,
      isRowSelectionEnabled: isRowSelectionEnabled,
      containsTabbableCell: containsTabbableCell,
      onSelectionChange: onTableSelectionChange,
      sortCriterion: sortCriterion,
      onSortCriterionChange: onSortCriterionChange,
      showFocusRing: showFocusRing,
      startStickyEdge: startStickyEdge,
      endStickyEdge: endStickyEdge,
      isResizeHover: resizeInfoRef.current.isResizeHover,
      isRtl: isRtl
    };
  };

  const getTableFooterProps = () => {
    const tabbableCell = getCurrentTabbableCell();
    const containsTabbableCell = tabbableCell?.type === 'footer';
    return {
      tableId: tableId,
      isRendered: hasFooters,
      columnsArray: columnsArray,
      hasVerticalGridlines: hasVerticalGridlines,
      focusedKey: currentCell?.type === 'footer' ? currentCell.columnKey : undefined,
      selectedKeys: selected.column,
      isColumnSelectionEnabled: isColumnSelectionEnabled,
      containsTabbableCell: containsTabbableCell,
      showFocusRing: showFocusRing,
      startStickyEdge: startStickyEdge,
      endStickyEdge: endStickyEdge,
      isRtl: isRtl
    };
  };

  const getTableDragIndicator = () => {
    if (columnResizingIndicator != null && !layoutInfo.isLayoutRequired) {
      let position = 0;
      for (let i = 0; i < columnsArray.length; i++) {
        position += layoutInfo.columnWidthsArray[i];
        if (columnsArray[i].key === columnResizingIndicator.key) {
          if (columnResizingIndicator.delta != null) {
            position += columnResizingIndicator.delta;
          }
          break;
        }
      }
      return <TableDragIndicator position={position} isRtl={isRtl} />;
    }
    return undefined;
  };

  const getTableStyle = () => {
    let widthString = '';
    let heightString = '';
    if (!layoutInfo.isLayoutRequired) {
      let totalWidth = 0;
      for (let i = 0; i < layoutInfo.columnWidthsArray.length; i++) {
        totalWidth += layoutInfo.columnWidthsArray[i];
      }
      widthString = `width: ${totalWidth}px; table-layout: fixed;`;
    } else {
      // shrink table down as much as possible when a 'contents' layout is required, otherwise
      // the sampled column widths will not reflect the true minimum sizes with line-clamping
      widthString = layout !== 'contents' ? 'width: 100%;' : 'width: 1px';
    }
    if (contentHeight != null) {
      heightString = data == null || data.length === 0 ? `min-height: ${contentHeight}px;` : '';
    }
    return `${widthString}${heightString}`;
  };

  const onStartTracking = useCallback(
    (detail: {
      activeElement: FocusableElement;
      hasFocusWithin: boolean;
      tabbableElements: FocusableElement[];
    }) => {
      handleEnterActionableMode(
        detail.hasFocusWithin,
        detail.tabbableElements,
        setCurrentTabbableCell
      );
    },
    [setCurrentTabbableCell]
  );

  const onFocusStartEdge = useCallback(
    (detail: { tabbableElements: FocusableElement[] }) => {
      handleWrapActionableFocus(true, detail.tabbableElements, setCurrentTabbableCell);
    },
    [setCurrentTabbableCell]
  );

  const onFocusEndEdge = useCallback(
    (detail: { tabbableElements: FocusableElement[] }) => {
      handleWrapActionableFocus(false, detail.tabbableElements, setCurrentTabbableCell);
    },
    [setCurrentTabbableCell]
  );

  return (
    <div
      {...testIdProps}
      ref={outerTableRef}
      aria-colcount={columnsArray.length}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      aria-rowcount={ariaRowCount}
      {...(selectionMode?.column === 'multiple' || selectionMode?.row === 'multiple'
        ? { 'aria-multiselectable': true }
        : {})}
      class={classes}
      role={'grid'}
      tabIndex={0}
      {...interactionManagerProps}>
      <FocusTracker
        styleClass={focusTrackerClasses}
        isDisabled={isCurrentTabbableCell(undefined)}
        onFocusStartEdge={onFocusStartEdge}
        onFocusEndEdge={onFocusEndEdge}
        onStartTracking={onStartTracking}>
        <table
          class={tableClasses}
          role={'presentation'}
          style={getTableStyle()}
          data-oj-table-inner-table={tableId}>
          <TableColGroup {...getTableColGroupProps()} />
          <TableHeader {...getTableHeaderProps()} />
          <TableBody {...getTableBodyProps()} />
          <TableFooter {...getTableFooterProps()} />
        </table>
        {getTableDragIndicator()}
      </FocusTracker>
    </div>
  );
});
